import { showNewMessage } from '@intercom/messenger-js-sdk'
import {
	FlowPage,
	H2,
	ICON_SIZE,
	PrimaryButton,
	SecondaryButton,
} from '@monthio/design-system'
import { TOKENS } from '@monthio/design-system/tokens'
import { Add } from '@monthio/icons'
import {
	COLORS,
	eventHubService,
	FlowContainer,
	LoadingScreen,
	MaterialIcons,
	ModalBody,
	trackingService,
	useModal,
} from '@monthio/react-components'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import type { BankError, BankSession } from '../../Bank.types'
import type { RootState } from '../../Redux/reducers'
import type { BankSessionState } from '../../Redux/reducers/consumerDataPullSessionReducers/bankSessionReducer'
import type { CloseConsumerDataPullSessionState } from '../../Redux/reducers/consumerDataPullSessionReducers/closeConsumerDataPullSessionReducer'
import type { FetchBankSessionsTransactionsState } from '../../Redux/reducers/consumerDataPullSessionReducers/fetchBankSessionsTransactionsReducer'

import { routeCodes } from '../../Bank.types'
import {
	clearErrors as reduxClearErrors,
	createBankSession as reduxCreateBankSession,
	getAllBankSessions as reduxGetAllBankSessions,
} from '../../Redux/actions/consumerDataPullSessionActions/bankSessionActions'
import { closeConsumerDataPullSession as reduxCloseConsumerDataPullSession } from '../../Redux/actions/consumerDataPullSessionActions/closeConsumerDataPullSessionActions'
import {
	clearFetchBankSessionsTransactionsErrors as reduxClearFetchBankSessionsTransactionsErrors,
	fetchBankSessionsTransactions as reduxFetchBankSessionsTransactions,
} from '../../Redux/actions/consumerDataPullSessionActions/fetchBankSessionsTransactions'
import { BankSessionStatus } from '../../Redux/reducers/consumerDataPullSessionReducers/bankSessionReducer'
import { FetchBankSessionsTransactionsStatus } from '../../Redux/reducers/consumerDataPullSessionReducers/fetchBankSessionsTransactionsReducer'
import { BankSessionListItem } from './BankSessionListItem'
import { BankSessionListContainer } from './bankSessionOverviewStyles'
import { FlowBottomWithConsent } from './BottomWithConsent'
import { MissingAccountsPrompt } from './MissingAccountsPrompt'

export const StyledAddIcon = styled(MaterialIcons.Add)`
	color: ${COLORS.PRIMARY};
`

export const ButtonIconAndTextWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: 12px;
	font-weight: 600;
	align-items: center;
`

const getAccountsLength = (sessions: BankSession[]) => {
	let accountsLength = 0
	sessions.forEach((session) => {
		accountsLength += session.accounts.length
	})
	return accountsLength
}

const setDeselectedAccountsInSessionStorage = (
	deselectedAccountIds: string[],
): void => {
	sessionStorage.setItem(
		'deselectedAccounts',
		JSON.stringify(deselectedAccountIds),
	)
}

const getDeselectedAccountsFromSessionStorage = (): string[] => {
	const deselectedAccounts = sessionStorage.getItem('deselectedAccounts')

	if (deselectedAccounts) {
		try {
			const parsedAccounts = JSON.parse(deselectedAccounts)

			if (
				Array.isArray(parsedAccounts) &&
				parsedAccounts.every((item) => typeof item === 'string')
			) {
				return parsedAccounts
			}
		} catch (error) {
			console.error(
				'Error parsing deselected accounts from sessionStorage',
				error,
			)
		}
	}

	return []
}

const DSAddBanksSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${TOKENS.space.xs};
`

export interface BankSessionPageProps {
	bankSessions: BankSessionState
	createBankSession: (
		bankId: number,
		consentCode: string,
		authUrl?: string,
	) => void
	fetchBankSessionsTransactionsState: FetchBankSessionsTransactionsState
	closeConsumerDataPullSessionState: CloseConsumerDataPullSessionState
	fetchBankSessionsTransactions: (
		deselectedAccountIds: string[],
		calculationReferenceDate?: Date,
	) => void
	clearBankSessionErrors: () => void
	closeConsumerDataPullSession: (shouldClearBudgetAndDebtData: boolean) => void
	requestBankSessions: () => void
	clearFetchBankSessionsTransactionsErrors: () => void
	sessionId: string | null
	hasExplicitConsent: boolean
	clientName: string
	calculationReferenceDate?: Date
}

export const BankSessionPage = (props: BankSessionPageProps): JSX.Element => {
	const {
		bankSessions,
		sessionId,
		createBankSession,
		fetchBankSessionsTransactions,
		clearBankSessionErrors,
		closeConsumerDataPullSession,
		closeConsumerDataPullSessionState,
		clearFetchBankSessionsTransactionsErrors,
		fetchBankSessionsTransactionsState,
		hasExplicitConsent,
		clientName,
		calculationReferenceDate,
	} = props

	const history = useHistory<{
		authUrl?: string
		consentCode?: string
		monthioInternalBankId?: number
	}>()
	const { t } = useTranslation()
	const { openModal, closeModal } = useModal()
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

	const [explicitConsentGiven, setExplicitConsentGiven] =
		useState<boolean>(!hasExplicitConsent)

	const [missingAccountPromptEnabled, setMissingAccountPromptEnabled] =
		useState<boolean>(true)
	const [displaySufficientDataModal, setDisplaySufficientDataModal] =
		useState<boolean>(false)
	const [deselectedAccountIds, setDeselectedAccountIds] = useState<string[]>([])

	const [sessions, setSessions] = useState<BankSession[]>([])

	useEffect(() => {
		const { consentCode, monthioInternalBankId, authUrl } =
			history.location.state || {}
		if (
			consentCode !== undefined &&
			monthioInternalBankId !== undefined &&
			authUrl !== undefined &&
			bankSessions.status !== BankSessionStatus.CREATING
		) {
			createBankSession(monthioInternalBankId, consentCode, authUrl)
		}
	}, [])

	useEffect(() => {
		const alreadyDeselectedAccounts =
			bankSessions.bankSessions?.flatMap((bs) =>
				bs.accounts.filter((a) => !a.selected),
			) || []

		const deselectedAccountsFromSession =
			getDeselectedAccountsFromSessionStorage()

		if (deselectedAccountsFromSession.length === 0) {
			updateDeselectedAccountIds(
				alreadyDeselectedAccounts.map((a) => a.accountId),
			)
		} else {
			const deselectedAccountsFiltered = deselectedAccountsFromSession.filter(
				(d) =>
					bankSessions.bankSessions
						?.flatMap((bs) => bs.accounts)
						.map((v) => v.accountId)
						.includes(d),
			)
			updateDeselectedAccountIds(deselectedAccountsFiltered)
		}
	}, [])

	const fetchTransactionsAndHandleSufficientDataCheck =
		useCallback(async () => {
			fetchBankSessionsTransactions(
				deselectedAccountIds,
				calculationReferenceDate,
			)
			setDisplaySufficientDataModal(true)
		}, [
			fetchBankSessionsTransactions,
			deselectedAccountIds,
			calculationReferenceDate,
			setDisplaySufficientDataModal,
			fetchBankSessionsTransactionsState.banksToAdd.length,
		])

	useEffect(() => {
		if (closeConsumerDataPullSessionState.closed && sessionId !== null) {
			eventHubService.onEvent({
				source: 'transactionClient',
				topic: 'moduleCompleted',
				payload: { module: 'bank', skipped: false },
			})
		}
	}, [closeConsumerDataPullSessionState.closed])

	useEffect(() => {
		if (
			!closeConsumerDataPullSessionState.closed &&
			fetchBankSessionsTransactionsState.succeeded &&
			sessionId !== null &&
			((fetchBankSessionsTransactionsState.TransactionDataValidation
				.hasEnoughTransactionsLastMonth &&
				fetchBankSessionsTransactionsState.TransactionDataValidation
					.hasEnoughTransactionsLastQuarter &&
				fetchBankSessionsTransactionsState.TransactionDataValidation
					.hasThreeMonthsOfTransactions &&
				fetchBankSessionsTransactionsState.banksToAdd.length == 0) ||
				!missingAccountPromptEnabled)
		) {
			closeConsumerDataPullSession(
				fetchBankSessionsTransactionsState.shouldClearBudgetAndDebtData,
			)
		}
	}, [
		closeConsumerDataPullSessionState.closed,
		missingAccountPromptEnabled,
		history,
		sessionId,
		fetchBankSessionsTransactionsState.succeeded,
	])

	useEffect(() => {
		setSessions(groupAccountsByBank(bankSessions))
	}, [bankSessions])

	const updateDeselectedAccountIds = (_deselectedAccountIds: string[]) => {
		setDeselectedAccountIds(_deselectedAccountIds)
		setDeselectedAccountsInSessionStorage(_deselectedAccountIds)
	}

	const handleAccountSelection = useCallback(
		(accountId: string, isDeselected: boolean) => {
			let deselectedAccounts = [...deselectedAccountIds]

			isDeselected
				? deselectedAccounts.push(accountId)
				: (deselectedAccounts = deselectedAccounts.filter(
						(x) => x != accountId,
					))

			updateDeselectedAccountIds(deselectedAccounts)
		},
		[deselectedAccountIds],
	)

	const handleAccountsSelection = useCallback(
		(e: { selected: string[]; deselected: string[] }) => {
			let deselectedAccounts = [...deselectedAccountIds]

			for (const accountId of e.deselected) {
				if (!deselectedAccounts.includes(accountId)) {
					deselectedAccounts.push(accountId)
				}
			}

			for (const accountIdSelected of e.selected) {
				deselectedAccounts = deselectedAccounts.filter(
					(accountId) => accountId != accountIdSelected,
				)
			}
			updateDeselectedAccountIds(deselectedAccounts)
		},
		[deselectedAccountIds],
	)

	const handleAddMoreBanks = useCallback(() => {
		setDisplaySufficientDataModal(false)
		setIsModalOpen(false)
		closeModal('prompt-modal')
		history.replace(routeCodes.banks)
	}, [])
	const handleOverrideSufficientDataCheck = useCallback(() => {
		setMissingAccountPromptEnabled(false)
		setIsModalOpen(false)
		closeModal('prompt-modal')
	}, [])

	useEffect(() => {
		const triggerModal = async (error?: BankError) => {
			let errorCode = error?.errorCode ?? 'otherError'
			let options = {}

			switch (error?.errorCode) {
				case 'PA_PL_1':
					errorCode = 'alreadyAddedAccountError'
					break
				case 'PA_FT_1':
					errorCode = 'fetchTransactionError'
					options = {
						bankName: error.data.BankName,
					}
					break
				case 'PA_TE_1':
					errorCode = 'tokenExpiredError'
					options = {
						bankName: error.data.BankName,
					}
					break
				default:
					errorCode = 'otherError'
					break
			}

			const errorKeys = {
				body: `bankSessionOverview-${errorCode}-text`,
			}

			const title = t('bankSessionOverview-otherError-title')
			const body = t(errorKeys.body, options)

			const bankSessionOverviewModalBody = () => (
				<ModalBody>
					<p>{body}</p>
					{errorCode === 'fetchTransactionError' ? (
						<PrimaryButton onClick={() => showNewMessage(body)}>
							{t('bankSessionOverview-fetchTransactionError-contactSupport')}
						</PrimaryButton>
					) : null}
				</ModalBody>
			)
			trackingService.trackEvent('bankSessionErrorModal', {
				errorCode,
				options,
				error,
			})
			await openModal({ bodyRender: bankSessionOverviewModalBody, title })
			history.replace(routeCodes.banks)
		}

		if (bankSessions.status === BankSessionStatus.CREATE_FAILURE) {
			triggerModal(bankSessions.error)
		}
		if (
			fetchBankSessionsTransactionsState.status ===
			FetchBankSessionsTransactionsStatus.FETCH_FAILURE
		) {
			triggerModal(fetchBankSessionsTransactionsState.error)
		}
		const emptyListOfAccountsReceivedErrorMessage = {
			name: 'emptyListOfAccountsReceived',
			errorCode: 'zeroAccounts',
			errorText: 'We received an empty list of accounts',
			data: {},
		}
		if (
			bankSessions.status === BankSessionStatus.CREATE_SUCCESS &&
			bankSessions.emptyListOfAccountsReceived
		) {
			triggerModal(emptyListOfAccountsReceivedErrorMessage)
		}
		if (closeConsumerDataPullSessionState.closeConsumerDataPullSessionError) {
			triggerModal(
				closeConsumerDataPullSessionState.closeConsumerDataPullSessionError,
			)
		}
	}, [
		bankSessions.status,
		bankSessions.error,
		clearBankSessionErrors,
		clearFetchBankSessionsTransactionsErrors,
		openModal,
		history,
		t,
		fetchBankSessionsTransactionsState.status,
		fetchBankSessionsTransactionsState.error,
		bankSessions.emptyListOfAccountsReceived,
	])

	useEffect(() => {
		trackingService.trackEvent('accountSelectionScreen', {
			accounts: getAccountsLength(sessions),
		})
	})

	useEffect(() => {
		return () => {
			clearBankSessionErrors()
			clearFetchBankSessionsTransactionsErrors()
		}
	}, [])

	useEffect(() => {
		if (
			fetchBankSessionsTransactionsState.succeeded &&
			(!(
				fetchBankSessionsTransactionsState.TransactionDataValidation
					.hasEnoughTransactionsLastMonth &&
				fetchBankSessionsTransactionsState.TransactionDataValidation
					.hasEnoughTransactionsLastQuarter &&
				fetchBankSessionsTransactionsState.TransactionDataValidation
					.hasThreeMonthsOfTransactions
			) ||
				fetchBankSessionsTransactionsState.banksToAdd.length > 0) &&
			displaySufficientDataModal &&
			missingAccountPromptEnabled
		) {
			setIsModalOpen(true)
		}
	}, [
		fetchBankSessionsTransactionsState.succeeded,
		fetchBankSessionsTransactionsState.TransactionDataValidation,
		fetchBankSessionsTransactionsState.banksToAdd.length,
		displaySufficientDataModal,
		missingAccountPromptEnabled,
	])

	const handleExplicitConsentChange = useCallback(() => {
		setExplicitConsentGiven((prev) => !prev)
	}, [])

	const onContinue = useCallback(async () => {
		trackingService.trackEvent('accountSelectionContinue', {
			accounts: getAccountsLength(sessions),
		})
		await fetchTransactionsAndHandleSufficientDataCheck()
		history.replace(routeCodes.bankSessionOverviewPage, {})
	}, [sessions, fetchTransactionsAndHandleSufficientDataCheck, history])

	const canContinue =
		deselectedAccountIds.length !== getAccountsLength(sessions) &&
		explicitConsentGiven

	return (
		<>
			{isModalOpen ? (
				<MissingAccountsPrompt
					onOpenChange={setIsModalOpen}
					clientName={clientName}
					handleContinue={handleOverrideSufficientDataCheck}
					triggerReasones={
						fetchBankSessionsTransactionsState.TransactionDataValidation
					}
					handleAddMoreBanks={handleAddMoreBanks}
					banksToAdd={fetchBankSessionsTransactionsState.banksToAdd.map(
						(b) => b.name,
					)}
				/>
			) : null}
			{bankSessions.status === BankSessionStatus.CREATING ||
			fetchBankSessionsTransactionsState.status ===
				FetchBankSessionsTransactionsStatus.FETCHING ||
			closeConsumerDataPullSessionState.closingConsumerDataPullSession ? (
				<FlowPage>
					<FlowPage.Content $isCentered>
						<LoadingScreen />
					</FlowPage.Content>
				</FlowPage>
			) : bankSessions.status === BankSessionStatus.CREATE_FAILURE ? (
				<FlowContainer />
			) : (
				<FlowPage>
					<FlowPage.Content $spacing="xl">
						<FlowPage.Header
							title={t('bankSessionOverview-moreAccounts-title')}
							description={t('bankSessionOverview-deselectAccounts-text')}
						/>
						<BankSessionListContainer data-testid="bankSessionList">
							{sessions.map((session) => {
								const accounts = session.accounts.map((account) => ({
									...account,
									selected: !deselectedAccountIds.includes(account.accountId),
								}))
								return (
									<BankSessionListItem
										bankId={session.bankId}
										deselectedAccountIds={deselectedAccountIds}
										key={session.bankId}
										accounts={accounts}
										handleAccountSelection={handleAccountSelection}
										handleAccountsSelection={handleAccountsSelection}
									/>
								)
							})}
						</BankSessionListContainer>
						<DSAddBanksSection>
							<H2>{t('bankSessionOverview-additionalAccounts-text')}</H2>
							<AddButton onClick={() => history.replace(routeCodes.banks)}>
								<Add height={16} />
								{t('bankSessionOverview-addBanks-button')}
							</AddButton>
						</DSAddBanksSection>
					</FlowPage.Content>
					<FlowBottomWithConsent
						isConsentGiven={explicitConsentGiven}
						isConsentRequired={hasExplicitConsent}
						onConsentGivenChange={handleExplicitConsentChange}
						onContinue={onContinue}
						canContinue={canContinue}
					/>
				</FlowPage>
			)}
		</>
	)
}

const AddButton = styled(SecondaryButton)`
	> svg {
		height: ${ICON_SIZE.m};
		aspect-ratio: 1;
	}
	${(props) => props.theme.breakpoints.up('sm')} {
		max-width: 250px;
	}
`

const groupAccountsByBank = (bankSessions: BankSessionState) => {
	const bankAccountsByBank = bankSessions.bankSessions?.reduce(
		(accumulator, currentValue) => {
			const bankSessionIndex = accumulator.findIndex(
				(x) => x.bankId == currentValue.bankId,
			)
			if (bankSessionIndex !== -1) {
				accumulator[bankSessionIndex]!.accounts = [
					...accumulator[bankSessionIndex]!.accounts,
					...currentValue.accounts,
				]
			} else {
				accumulator.push({
					bankId: currentValue.bankId,
					accounts: [...currentValue.accounts],
				})
			}

			return accumulator
		},
		[] as BankSession[],
	)
	return bankAccountsByBank || []
}

export default connect(
	(state: RootState) => {
		return {
			sessionId: state.consumerAuthentication.sessionId,
			bankSessions: state.bankSessions,
			fetchBankSessionsTransactionsState:
				state.fetchBankSessionsTransactionsState,
			closeConsumerDataPullSessionState: state.closeConsumerDataPullSession,
		}
	},
	(dispatch: any) => {
		return {
			createBankSession: (
				bankId: number,
				consentCode: string,
				authUrl?: string,
			) => dispatch(reduxCreateBankSession(bankId, consentCode, authUrl)),
			closeConsumerDataPullSession: (shouldClearBudgetAndDebtData: boolean) =>
				dispatch(
					reduxCloseConsumerDataPullSession(shouldClearBudgetAndDebtData),
				),
			clearBankSessionErrors: () => dispatch(reduxClearErrors()),
			requestBankSessions: () => dispatch(reduxGetAllBankSessions()),
			fetchBankSessionsTransactions: (
				deselectedAccountIds: string[],
				calculationReferenceDate?: Date,
			) =>
				dispatch(
					reduxFetchBankSessionsTransactions(
						deselectedAccountIds,
						calculationReferenceDate,
					),
				),
			clearFetchBankSessionsTransactionsErrors: () =>
				dispatch(reduxClearFetchBankSessionsTransactionsErrors()),
		}
	},
)(BankSessionPage)
